import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError, downloadCsv } from "@/lib/api";
import { useOutletContext } from "react-router-dom";
import { PageHeader, StatusBadge } from "@/components/Shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { LogIn, LogOut, Loader2, Timer, Download, MapPin, Clock, Trash2, Plus, UserCheck } from "lucide-react";
import { toast } from "sonner";

const MANUAL_STATUSES = ["Present", "Absent", "Half Day", "On Duty", "Work From Home"];

function todayStr() { return new Date().toISOString().slice(0, 10); }

function ManualAttendance() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const empty = { email: "", date: todayStr(), status: "Present", checkin: "09:30", checkout: "18:30", note: "" };
  const [form, setForm] = useState(empty);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const { data: employees = [] } = useQuery({
    queryKey: ["employees"], queryFn: async () => (await api.get("/employees")).data,
  });
  const mut = useMutation({
    mutationFn: async () => (await api.post("/attendance/manual", form)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["attendance-logs"] }); setOpen(false); setForm(empty); toast.success("Attendance recorded"); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const isAbsent = form.status === "Absent";
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="rounded-lg" data-testid="manual-attendance-open"><UserCheck className="w-4 h-4 mr-1" /> Mark Attendance</Button>
      </DialogTrigger>
      <DialogContent className="max-w-md" data-testid="manual-attendance-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><UserCheck className="w-4 h-4 text-accent" /> Mark Attendance Manually</DialogTitle>
          <DialogDescription>Record or correct attendance for an employee. This bypasses the geofence and overwrites any existing entry for that date.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1.5"><Label>Employee</Label>
            <Select value={form.email} onValueChange={(v) => set("email", v)}>
              <SelectTrigger data-testid="manual-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
              <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.email}>{e.name} ({e.employee_id})</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="manual-date-input" /></div>
            <div className="space-y-1.5"><Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="manual-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{MANUAL_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          {!isAbsent && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Check In</Label><Input type="time" value={form.checkin} onChange={(e) => set("checkin", e.target.value)} data-testid="manual-checkin-input" /></div>
              <div className="space-y-1.5"><Label>Check Out</Label><Input type="time" value={form.checkout} onChange={(e) => set("checkout", e.target.value)} data-testid="manual-checkout-input" /></div>
            </div>
          )}
          <div className="space-y-1.5"><Label>Note (optional)</Label><Textarea value={form.note} onChange={(e) => set("note", e.target.value)} placeholder="e.g. Forgot to punch in / on client site" data-testid="manual-note-input" /></div>
        </div>
        <DialogFooter>
          <Button onClick={() => mut.mutate()} disabled={mut.isPending || !form.email} className="rounded-lg" data-testid="manual-attendance-save">
            {mut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Attendance"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function fmtTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
}

function distanceM(lat1, lng1, lat2, lng2) {
  const R = 6371000, rad = (d) => (d * Math.PI) / 180;
  const dlat = rad(lat2 - lat1), dlng = rad(lng2 - lng1);
  const a = Math.sin(dlat / 2) ** 2 + Math.cos(rad(lat1)) * Math.cos(rad(lat2)) * Math.sin(dlng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

function PunchWidget({ clock, today, checkinMut, checkoutMut }) {
  const [pos, setPos] = useState(null);
  const [geoErr, setGeoErr] = useState("");
  const { data: office } = useQuery({
    queryKey: ["office-config"], queryFn: async () => (await api.get("/attendance/office-config")).data,
  });

  useEffect(() => {
    if (!navigator.geolocation) { setGeoErr("Location not supported by this browser"); return; }
    const id = navigator.geolocation.watchPosition(
      (p) => { setGeoErr(""); setPos({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }); },
      (e) => setGeoErr(e.message || "Location access denied"),
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 15000 },
    );
    return () => navigator.geolocation.clearWatch(id);
  }, []);

  const status = today?.status || "Not Checked In";
  const geoEnabled = office?.enabled;
  const dist = pos && office?.lat != null ? distanceM(pos.lat, pos.lng, office.lat, office.lng) : null;
  const withinRange = !geoEnabled || (dist != null && dist - (pos?.accuracy || 0) <= office.radius_m);
  const locReady = !geoEnabled || !!pos;
  const payload = () => (pos ? { lat: pos.lat, lng: pos.lng, accuracy: pos.accuracy } : {});

  const alreadyIn = today?.checkin && !today?.checkout;
  const done = !!today?.checkout;

  return (
    <Card className="p-6 sm:p-8 border border-border rounded-xl mb-6 bg-gradient-to-br from-card to-secondary/40" data-testid="punch-widget">
      <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
        <div className="text-center sm:text-left">
          <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{clock.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })}</p>
          <p className="font-heading text-5xl font-extrabold tracking-tight tabular-nums mt-1">{clock.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</p>
          <div className="mt-3"><StatusBadge status={status === "Not Checked In" ? "Checked Out" : status} /></div>
        </div>
        <div className="flex flex-col gap-3">
          <div className="flex gap-4 text-center">
            <div><p className="text-xs text-muted-foreground">In</p><p className="font-mono font-semibold">{fmtTime(today?.checkin)}</p></div>
            <div><p className="text-xs text-muted-foreground">Out</p><p className="font-mono font-semibold">{fmtTime(today?.checkout)}</p></div>
            <div><p className="text-xs text-muted-foreground">Hours</p><p className="font-mono font-semibold">{today?.hours ? `${today.hours}h` : "—"}</p></div>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={() => checkinMut.mutate(payload())}
              disabled={checkinMut.isPending || alreadyIn || done || !withinRange || !locReady}
              data-testid="checkin-button" className="rounded-lg font-semibold h-11 px-6 active:scale-[0.98]"
            >
              {checkinMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><LogIn className="w-4 h-4 mr-1.5" /> Check In</>}
            </Button>
            <Button
              onClick={() => checkoutMut.mutate(payload())}
              disabled={checkoutMut.isPending || !today?.checkin || done || !withinRange || !locReady}
              variant="secondary" data-testid="checkout-button" className="rounded-lg font-semibold h-11 px-6 active:scale-[0.98]"
            >
              {checkoutMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><LogOut className="w-4 h-4 mr-1.5" /> Check Out</>}
            </Button>
          </div>
        </div>
      </div>
      {geoEnabled && (
        <div className="mt-5 pt-4 border-t border-border flex items-start gap-2 text-sm" data-testid="geofence-status">
          <MapPin className={`w-4 h-4 mt-0.5 ${withinRange ? "text-emerald-600" : "text-destructive"}`} />
          <div>
            {geoErr ? (
              <p className="text-destructive">{geoErr}. Allow location access to punch in/out.</p>
            ) : !pos ? (
              <p className="text-muted-foreground">Locating you… allow location access to enable the buttons.</p>
            ) : withinRange ? (
              <p className="text-emerald-700">You're within the office premises (~{Math.round(dist)}m). Punch in/out enabled.</p>
            ) : (
              <p className="text-destructive">You're ~{Math.round(dist)}m from the office. Move within {office.radius_m}m to punch in/out.</p>
            )}
            <p className="text-xs text-muted-foreground">Check-in/out is only allowed within {office?.radius_m}m of the office.</p>
          </div>
        </div>
      )}
    </Card>
  );
}

function ShiftManager() {
  const qc = useQueryClient();
  const [form, setForm] = useState({ name: "", start_time: "09:30", end_time: "18:30", grace_minutes: 10 });
  const { data: shifts = [] } = useQuery({ queryKey: ["shifts"], queryFn: async () => (await api.get("/shifts")).data });
  const createMut = useMutation({
    mutationFn: async () => (await api.post("/shifts", form)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["shifts"] }); setForm({ name: "", start_time: "09:30", end_time: "18:30", grace_minutes: 10 }); toast.success("Shift added"); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const delMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/shifts/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["shifts"] }); qc.invalidateQueries({ queryKey: ["employees"] }); toast.success("Shift removed"); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  return (
    <Card className="border border-border rounded-xl overflow-hidden mb-6" data-testid="shift-manager">
      <div className="px-5 py-4 border-b border-border flex items-center gap-2"><Clock className="w-4 h-4 text-accent" /><h3 className="font-heading font-semibold">Shift Management</h3></div>
      <div className="p-5 space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 items-end">
          <div className="space-y-1.5 col-span-2 sm:col-span-1"><Label className="text-xs">Shift name</Label><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Morning" data-testid="shift-name-input" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Start</Label><Input type="time" value={form.start_time} onChange={(e) => setForm((f) => ({ ...f, start_time: e.target.value }))} data-testid="shift-start-input" /></div>
          <div className="space-y-1.5"><Label className="text-xs">End</Label><Input type="time" value={form.end_time} onChange={(e) => setForm((f) => ({ ...f, end_time: e.target.value }))} data-testid="shift-end-input" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Grace (min)</Label><Input type="number" min="0" value={form.grace_minutes} onChange={(e) => setForm((f) => ({ ...f, grace_minutes: Number(e.target.value) }))} data-testid="shift-grace-input" /></div>
          <Button onClick={() => createMut.mutate()} disabled={createMut.isPending || !form.name} className="rounded-lg" data-testid="shift-add-button"><Plus className="w-4 h-4 mr-1" /> Add</Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {shifts.length === 0 && <p className="text-sm text-muted-foreground">No shifts yet. Add one, then assign it to employees in the Directory.</p>}
          {shifts.map((s) => (
            <span key={s.id} className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary/50 px-3 py-1 text-sm" data-testid={`shift-chip-${s.id}`}>
              <span className="font-medium">{s.name}</span>
              <span className="font-mono text-xs text-muted-foreground">{s.start_time}–{s.end_time} (+{s.grace_minutes}m)</span>
              <button onClick={() => delMut.mutate(s.id)} className="text-destructive" data-testid={`shift-delete-${s.id}`} title="Delete shift"><Trash2 className="w-3.5 h-3.5" /></button>
            </span>
          ))}
        </div>
      </div>
    </Card>
  );
}

export default function Attendance() {
  const { role } = useOutletContext();
  const qc = useQueryClient();
  const [clock, setClock] = useState(new Date());

  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const { data: today } = useQuery({
    queryKey: ["attendance-today"], queryFn: async () => (await api.get("/attendance/today")).data,
  });
  const { data: logs = [], isLoading } = useQuery({
    queryKey: ["attendance-logs"], queryFn: async () => (await api.get("/attendance")).data,
  });

  const invalidate = () => { qc.invalidateQueries({ queryKey: ["attendance-today"] }); qc.invalidateQueries({ queryKey: ["attendance-logs"] }); };
  const checkinMut = useMutation({
    mutationFn: async (loc) => (await api.post("/attendance/checkin", loc)).data,
    onSuccess: () => { invalidate(); toast.success("Checked in!"); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const checkoutMut = useMutation({
    mutationFn: async (loc) => (await api.post("/attendance/checkout", loc)).data,
    onSuccess: () => { invalidate(); toast.success("Checked out!"); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const isEmployee = role === "employee";

  return (
    <div>
      <PageHeader title="Attendance & Timesheets" subtitle={isEmployee ? "Punch in / out within the office premises." : "Organisation attendance, shifts and logs."}>
        {!isEmployee && (
          <div className="flex gap-2">
            <ManualAttendance />
            <Button variant="outline" size="sm" className="rounded-lg" onClick={() => downloadCsv("/export/attendance.csv", "skanovix_attendance.csv")} data-testid="attendance-export-button">
              <Download className="w-4 h-4 mr-1" /> CSV
            </Button>
          </div>
        )}
      </PageHeader>

      {isEmployee && <PunchWidget clock={clock} today={today} checkinMut={checkinMut} checkoutMut={checkoutMut} />}
      {role === "hr" && <ShiftManager />}

      <Card className="border border-border rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-border flex items-center gap-2">
          <Timer className="w-4 h-4 text-accent" /><h3 className="font-heading font-semibold">Attendance Logs</h3>
        </div>
        {isLoading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  {!isEmployee && <TableHead>Employee</TableHead>}
                  <TableHead>Check In</TableHead><TableHead>Check Out</TableHead>
                  <TableHead className="text-right">Hours</TableHead><TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No records yet.</TableCell></TableRow>}
                {logs.map((l) => (
                  <TableRow key={l.id} data-testid={`attendance-row-${l.id}`}>
                    <TableCell className="font-mono text-xs">{l.date}</TableCell>
                    {!isEmployee && <TableCell className="font-medium">{l.employee_name}</TableCell>}
                    <TableCell className="font-mono">{fmtTime(l.checkin)}</TableCell>
                    <TableCell className="font-mono">{fmtTime(l.checkout)}</TableCell>
                    <TableCell className="text-right font-mono">{l.hours ? `${l.hours}h` : "—"}</TableCell>
                    <TableCell><StatusBadge status={l.status} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
